import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CampaignPlanStep } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  extractActivationCapExceeded,
  type ActivationCapWarning,
} from "@shared-core/data/validation";
import {
  ACTIVATE_NOW_CONFIRM_LABELS,
  ACTIVATE_NOW_LABELS,
  activateNowConfirmation,
  type ActivatableNowChannel,
} from "@shared-core/domain/campaign-step-activation";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { failureLogFields, readCampaignActionError } from "./actionErrors";

/** How many people left out the dialog reads to say why; beyond that it
 *  gives only the count. Matches the panel's own recipient list. */
const NOT_INCLUDED_READ = 100;

type Reach =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "loaded"; sentence: string };

/**
 * "Mark ready now" on a draft mailing, "Open now" on a draft call round:
 * the same activation the daily sweep would run for this one step. The
 * dialog first says who it will go to, from the step's current recipients
 * (the same rules activation freezes with), then activates on confirm. The
 * server keeps every rule; a refusal (no tiers, a follow-up whose mailing
 * isn't posted, the frequency cap) is shown as it gives it.
 */
export function ActivateStepNow({
  campaignId,
  step,
  channel,
  onChanged,
}: {
  campaignId: string;
  step: CampaignPlanStep;
  channel: ActivatableNowChannel;
  onChanged: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [open, setOpen] = useState(false);
  const [reach, setReach] = useState<Reach>({ kind: "loading" });
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capWarnings, setCapWarnings] = useState<ActivationCapWarning[]>([]);
  // Set when the call round opened but some tickets didn't go through: the
  // plan is refreshed once the dialog closes, so confirming again (which
  // raises the rest) stays possible until then.
  const [openedPartly, setOpenedPartly] = useState(false);
  const ids = { campaign_id: campaignId, step_id: step.id };
  const isMailing = channel === "direct_mail";

  async function loadReach() {
    setReach({ kind: "loading" });
    try {
      const [included, notIncluded] = await Promise.all([
        dataAdapter.listCampaignStepRecipients(step.id, {
          included: true,
          pageSize: 1,
        }),
        dataAdapter.listCampaignStepRecipients(step.id, {
          included: false,
          pageSize: NOT_INCLUDED_READ,
        }),
      ]);
      setReach({
        kind: "loaded",
        sentence: activateNowConfirmation(
          channel,
          included.total,
          notIncluded.total,
          notIncluded.items.flatMap((r) =>
            r.skip_reason ? [r.skip_reason] : [],
          ),
        ),
      });
    } catch (e) {
      console.error(
        "marketing.campaign.activate_now.reach_failed",
        failureLogFields(e, ids),
      );
      setReach({ kind: "failed" });
    }
  }

  function openDialog() {
    setError(null);
    setCapWarnings([]);
    setOpen(true);
    void loadReach();
  }

  function closeDialog() {
    if (activating) return;
    setOpen(false);
    if (openedPartly) {
      setOpenedPartly(false);
      onChanged();
    }
  }

  async function handleConfirm(confirmOverCap: boolean) {
    setActivating(true);
    setError(null);
    try {
      await dataAdapter.activateMarketingCampaignStep(campaignId, step.id, {
        confirmOverCap,
      });
      setOpen(false);
      setOpenedPartly(false);
      onChanged();
    } catch (e) {
      const capExceeded = extractActivationCapExceeded(e);
      if (capExceeded) {
        setCapWarnings(capExceeded.warnings);
        return;
      }
      console.error(
        "marketing.campaign.activate_now.failed",
        failureLogFields(e, ids),
      );
      // A call round whose tickets didn't all go through is open already
      // (502): its reason says so, and confirming again raises the rest.
      const partial =
        e instanceof ApiError && e.status === 502 && e.detail ? e.detail : null;
      setError(
        partial ??
          readCampaignActionError(
            e,
            isMailing
              ? "Couldn't mark this mailing ready. Please try again."
              : "Couldn't open this call round. Please try again.",
          ).summary,
      );
      if (partial) setOpenedPartly(true);
    } finally {
      setActivating(false);
    }
  }

  const title = isMailing
    ? "Mark this mailing ready?"
    : "Open this call round?";
  return (
    <>
      {/* A step that can't freeze yet says why beside it (no tiers chosen,
          the step it follows hasn't gone out). */}
      <Button
        size="sm"
        onClick={openDialog}
        disabled={step.no_recipients_reason !== null}
      >
        {ACTIVATE_NOW_LABELS[channel]}
      </Button>
      <Dialog open={open} onOpenChange={(next) => !next && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                {reach.kind === "loading" && <p>Checking who it goes to…</p>}
                {reach.kind === "failed" && (
                  <p>
                    Couldn't check who it goes to. You can still go ahead: the
                    recipients are worked out when it is{" "}
                    {isMailing ? "marked ready" : "opened"}.
                  </p>
                )}
                {reach.kind === "loaded" && <p>{reach.sentence}</p>}
                <p>
                  {isMailing
                    ? "Its mailing file is then ready to download."
                    : "Each of them gets a call ticket, for their rep or, with no rep, the campaign owner."}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>

          {capWarnings.length > 0 && (
            <div className="space-y-2 text-sm">
              <p className="font-medium">
                {capWarnings.length} contact
                {capWarnings.length === 1 ? " is" : "s are"} at the frequency
                cap
              </p>
              <p className="text-muted-foreground">
                They have already had 3 or more campaigns in the last 30 days.
                Confirm you still want to include them.
              </p>
              <ul className="list-disc pl-5 max-h-48 overflow-y-auto">
                {capWarnings.map((w) => (
                  <li key={w.personId}>
                    {w.personName} — {w.recentCampaignCount} campaigns in the
                    last 30 days
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-700 dark:text-red-300" role="alert">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={activating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleConfirm(capWarnings.length > 0)}
              disabled={activating || reach.kind === "loading"}
            >
              {activating
                ? "Saving…"
                : capWarnings.length > 0
                  ? `${ACTIVATE_NOW_CONFIRM_LABELS[channel]} anyway`
                  : ACTIVATE_NOW_CONFIRM_LABELS[channel]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
